import { type ReactNode, useEffect, useRef } from 'react'
import { useStrings } from '../i18n'

/**
 * The shell both full-screen surfaces share: the about page and the voltage
 * methodology write-up.
 *
 * Escape and a backdrop click close it, focus moves to the close button on
 * open and returns to whatever opened it on close, and Tab is kept inside the
 * dialog - the methodology modal is opened from a badge buried in a scrolling
 * panel, and losing the caret back into that panel is disorienting.
 */
export const Overlay = ({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) => {
  const strings = useStrings()
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const opener = document.activeElement
    closeRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      // Inputs are in the list because the card editor is a form: without them
      // the first and last focusable would be buttons, and Tab off the last
      // checkbox would land behind the dialog, on a page the visitor cannot see.
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable === undefined || focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (first === undefined || last === undefined) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [onClose])

  return (
    <div className="ev-overlay">
      {/* A real button rather than a click-handling div: clicking away to
          dismiss is a close control, so it should announce itself as one. */}
      <button
        type="button"
        className="ev-overlay__backdrop"
        onClick={onClose}
        aria-label={strings.common.close}
      />

      <div
        className="ev-overlay__dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="ev-overlay__header">
          <h1 className="ev-overlay__title">{title}</h1>
          <button
            type="button"
            className="ev-overlay__close"
            onClick={onClose}
            ref={closeRef}
            aria-label={strings.common.close}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="ev-overlay__body">{children}</div>
      </div>
    </div>
  )
}
